import { Tabs } from '@mui/material'
import Typography from '@mui/material/Typography'
import { MainApi } from 'api/MainApi'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import Loader from 'components/Loader/Loader'
import { StyledTab } from 'components/StyledTab/StyledTab'
import TabPanel from 'components/TabPanel/TabPanel'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MISSION_CALCULATING_STATUSES, PROGRESS_TYPES } from 'utils/constants'
import { IMissionInfo } from 'utils/interfaces'

import MissionsList from './MissionsList/MissionsList'
import ScenariosList from './ScenariosList/ScenariosList'

import style from './SelectMissionModal.module.css'

interface IDialogProps {
  selectedMissionId?: number
  isOpen: boolean
  robotType: string | undefined
  only_ok_missions?: boolean
  showScenariosTab?: boolean
  forceScenariosOnly?: boolean
  allowEmptyScenarios?: boolean
  /** When true, hide scenarios with mixed mission types (use for task assignment) */
  hideInconsistentScenarios?: boolean
  onConfirmClick: (mission: IMissionInfo | any) => void
  onClose: () => void
}

const SelectMissionModal = ({
  isOpen,
  robotType,
  only_ok_missions = true,
  showScenariosTab = false,
  forceScenariosOnly = false,
  allowEmptyScenarios = false,
  hideInconsistentScenarios = false,
  onConfirmClick,
  onClose,
  selectedMissionId,
}: React.PropsWithChildren<IDialogProps>) => {
  const { t } = useTranslation()
  const [loadingProgress, setLoadingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [missions, setMissions] = useState<IMissionInfo[]>([])
  const [scenarios, setScenarios] = useState<any[]>([])
  const [selectedMission, setSelectedMission] = useState<IMissionInfo | any | undefined>(undefined)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [activeTab, setActiveTab] = useState(forceScenariosOnly ? 1 : 0) // 0 for Missions, 1 for Scenarios
  const [hasMore, setHasMore] = useState(true)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const PAGE_SIZE = 20

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const fetchData = async (pageNum: number, search: string, isAppend = false) => {
    try {
      if (!isAppend) {
        setLoadingProgress(PROGRESS_TYPES.WORK)
      } else {
        setIsFetchingMore(true)
      }

      let responseCount = 0

      if (activeTab === 0) {
        const { results, count } = await MainApi.fetchMissions(pageNum, PAGE_SIZE, robotType, search || undefined)
        const filteredResults = only_ok_missions
          ? results.filter(mission => mission.status === MISSION_CALCULATING_STATUSES.OK)
          : results

        if (isAppend) {
          setMissions(prev => [...prev, ...filteredResults])
        } else {
          setMissions(filteredResults)
        }
        responseCount = count || 0
      } else {
        const { results, count } = await MainApi.fetchScenarios(pageNum, PAGE_SIZE, search || undefined, robotType)

        if (isAppend) {
          setScenarios(prev => [...prev, ...results])
        } else {
          setScenarios(results)
        }
        responseCount = count || 0
      }

      setHasMore(pageNum * PAGE_SIZE < responseCount)
      setPage(pageNum)

      if (!isAppend) {
        setLoadingProgress(PROGRESS_TYPES.SUCCESS)
      }
    } catch (error) {
      if (!isAppend) {
        setLoadingProgress(PROGRESS_TYPES.ERROR)
      }
    } finally {
      setIsFetchingMore(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      fetchData(1, debouncedSearchQuery, false)
    } else {
      setSelectedMission(undefined)
      setSearchQuery('')
      setDebouncedSearchQuery('')
      setMissions([])
      setScenarios([])
      setPage(1)
      setHasMore(true)
      setActiveTab(forceScenariosOnly ? 1 : 0)
      setLoadingProgress(PROGRESS_TYPES.IDLE)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, debouncedSearchQuery, activeTab])

  const loadMore = () => {
    if (loadingProgress === PROGRESS_TYPES.SUCCESS && hasMore && !isFetchingMore) {
      fetchData(page + 1, debouncedSearchQuery, true)
    }
  }

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      loadMore()
    }
  }

  const handleConfirm = () => {
    if (activeTab === 0) {
      onConfirmClick(selectedMission)
    } else {
      if (forceScenariosOnly) {
        onConfirmClick(selectedMission)
        return
      }
      if (!selectedMission || selectedMission.missions_ids?.length === 0) return
      const fakeMission = {
        id: selectedMission.id,
        is_scenario: true,
        name: selectedMission.name,
        missions_ids: selectedMission.missions_ids,
        robot_type: selectedMission.robot_type,
        status: MISSION_CALCULATING_STATUSES.OK,
      }
      onConfirmClick(fakeMission as any)
    }
  }

  return (
    <ConfirmDialog
      isOpen={isOpen}
      className={style.root}
      onClose={onClose}
      onConfirmClick={handleConfirm}
      title={t('robot.modal_select_mission_title')}
      confirmText={t('robot.modal_select_mission_confirm')}
      confirmButtonDisabled={!selectedMission}
    >
      <div className={style.content}>
        {showScenariosTab && !forceScenariosOnly && (
          <Tabs
            value={activeTab}
            onChange={(e, newValue: number) => {
              setActiveTab(newValue)
              setSelectedMission(undefined)
              setSearchQuery('')
            }}
            variant="fullWidth"
            indicatorColor="primary"
            sx={{ height: 45, minHeight: 45, marginBottom: '16px' }}
            centered
          >
            <StyledTab
              label="Missions"
              sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}
              disableRipple
            />
            <StyledTab
              label="Scenarios"
              sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}
              disableRipple
            />
          </Tabs>
        )}
        <input
          className={style.searchInput}
          type="text"
          placeholder={t('common.search') + '...'}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{ marginBottom: '16px' }}
        />
        {loadingProgress === PROGRESS_TYPES.WORK && <Loader />}

        <TabPanel value={activeTab} index={0} disablePadding={true}>
          {loadingProgress === PROGRESS_TYPES.SUCCESS && missions.length > 0 && (
            <MissionsList
              missions={missions}
              onSelect={mission => setSelectedMission(mission)}
              selectedMission={selectedMission}
              onScrollBottom={loadMore}
            />
          )}
          {loadingProgress === PROGRESS_TYPES.SUCCESS && missions.length === 0 && (
            <Typography variant="body1" align="center">
              {t('robot.modal_missions_empty')}
            </Typography>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={1} disablePadding={true}>
          {loadingProgress === PROGRESS_TYPES.SUCCESS && scenarios.length > 0 && (
            <ScenariosList
              scenarios={scenarios}
              onSelect={scenario => setSelectedMission(scenario)}
              selectedScenario={selectedMission}
              onScrollBottom={loadMore}
              allowEmptyScenarios={allowEmptyScenarios}
              hideInconsistentScenarios={hideInconsistentScenarios}
            />
          )}
          {loadingProgress === PROGRESS_TYPES.SUCCESS && scenarios.length === 0 && (
            <Typography variant="body1" align="center">
              No scenarios found
            </Typography>
          )}
        </TabPanel>

        {isFetchingMore && (
          <div style={{ padding: '10px', textAlign: 'center' }}>
            <Loader />
          </div>
        )}
      </div>
    </ConfirmDialog>
  )
}

export default SelectMissionModal
